import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "slide", "dot"]
  static values = { rtl: { type: Boolean, default: false } }

  connect() {
    this.slideCount = this.slideTargets.length
    if (this.slideCount === 0) return

    if (this.rtlValue) {
      this.currentIndex = this.slideCount - 1
    } else {
      this.currentIndex = 0
    }

    this.slideTo(this.currentIndex, false)
    this.startAutoPlay()

    // Touch/swipe support
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.trackTarget.addEventListener("touchstart", this.handleTouchStart, { passive: true })
    this.trackTarget.addEventListener("touchend", this.handleTouchEnd, { passive: true })

    // Pause on hover
    this.handleMouseEnter = () => this.stopAutoPlay()
    this.handleMouseLeave = () => this.startAutoPlay()
    this.element.addEventListener("mouseenter", this.handleMouseEnter)
    this.element.addEventListener("mouseleave", this.handleMouseLeave)

    // Pause on focus (keyboard accessibility)
    this.handleFocusIn = () => this.stopAutoPlay()
    this.handleFocusOut = () => this.startAutoPlay()
    this.element.addEventListener("focusin", this.handleFocusIn)
    this.element.addEventListener("focusout", this.handleFocusOut)
  }

  disconnect() {
    this.stopAutoPlay()

    if (this.trackTarget) {
      this.trackTarget.removeEventListener("touchstart", this.handleTouchStart)
      this.trackTarget.removeEventListener("touchend", this.handleTouchEnd)
    }

    this.element.removeEventListener("mouseenter", this.handleMouseEnter)
    this.element.removeEventListener("mouseleave", this.handleMouseLeave)
    this.element.removeEventListener("focusin", this.handleFocusIn)
    this.element.removeEventListener("focusout", this.handleFocusOut)
  }

  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.slideTo(index, true)
    this.restartAutoPlay()
  }

  slideTo(index, animate = true) {
    this.currentIndex = index

    if (!animate) {
      this.trackTarget.style.transition = "none"
    } else {
      this.trackTarget.style.transition = "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
    }

    const offset = -(index * 100)
    this.trackTarget.style.transform = `translateX(${offset}%)`

    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle("active", i === index)
    })
  }

  next() {
    let nextIndex
    if (this.rtlValue) {
      nextIndex = this.currentIndex - 1
      if (nextIndex < 0) nextIndex = this.slideCount - 1
    } else {
      nextIndex = (this.currentIndex + 1) % this.slideCount
    }
    this.slideTo(nextIndex, true)
  }

  prev() {
    let prevIndex
    if (this.rtlValue) {
      prevIndex = (this.currentIndex + 1) % this.slideCount
    } else {
      prevIndex = this.currentIndex - 1
      if (prevIndex < 0) prevIndex = this.slideCount - 1
    }
    this.slideTo(prevIndex, true)
  }

  startAutoPlay() {
    this.stopAutoPlay()
    this.autoPlayTimer = setInterval(() => this.next(), 3500)
  }

  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer)
      this.autoPlayTimer = null
    }
  }

  restartAutoPlay() {
    this.stopAutoPlay()
    this.startAutoPlay()
  }

  // Touch/swipe handlers (pattern from onboarding_controller.js)
  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX
  }

  handleTouchEnd(event) {
    const touchEndX = event.changedTouches[0].screenX
    const diff = this.touchStartX - touchEndX
    const threshold = 60

    if (Math.abs(diff) < threshold) return

    // Swipe left (positive diff) = next, swipe right (negative diff) = prev
    // RTL reverses the direction
    if (this.rtlValue) {
      if (diff > 0) {
        this.prev()
      } else {
        this.next()
      }
    } else {
      if (diff > 0) {
        this.next()
      } else {
        this.prev()
      }
    }

    this.restartAutoPlay()
  }
}
